import { formatFileSizeMb } from "@lib/text"
import * as m from "@paraglide/messages"
import type { AttachmentFile, ContactFormData } from "@site-types/send-mail"
import { useEffect, useState } from "react"
import type { FieldErrors, UseFormSetValue } from "react-hook-form"
import { ACCEPTED_ATTACHMENT_TYPES, ATTACHMENT_SIZE_LIMIT } from "../constants"
import { FieldTooltip } from "../FieldTooltip"
import { useSendMailLang } from "../lang-context"

type AttachmentInputProps = {
  toolTip: boolean
  errorMessage: string
  attachments: AttachmentFile[]
  setValue: UseFormSetValue<ContactFormData>
  errors?: FieldErrors<ContactFormData>
}

export const AttachmentInput = ({
  toolTip,
  errorMessage,
  attachments,
  setValue,
  errors,
}: AttachmentInputProps) => {
  const lang = useSendMailLang()

  const [errorFileType, setErrorFileType] = useState(false)
  const [errorLargeAttachment, setErrorLargeAttachment] = useState(false)

  const handleAttachmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target
    const fileList = Array.from(files ?? []) as File[]
    const acceptedTypes = ACCEPTED_ATTACHMENT_TYPES
    const filteredFiles = fileList.filter((file) => acceptedTypes.includes(file.type))
    const invalidFiles = fileList.filter((file) => !acceptedTypes.includes(file.type))
    setErrorFileType(invalidFiles.length > 0)
    if (filteredFiles.length === 0) return
    let currentAttachments = Array.isArray(attachments) ? [...attachments] : []
    if (currentAttachments.length >= 2) return
    const filesToProcess = filteredFiles.slice(0, 2 - currentAttachments.length)
    filesToProcess.forEach((file) => {
      const reader = new FileReader()
      reader.onload = () => {
        const fileContent = (reader.result as string).split(",")[1]
        const next = [
          ...currentAttachments,
          {
            name: file.name,
            content: fileContent,
            size: file.size,
            type: file.type,
            lastModified: file.lastModified,
          },
        ]
        currentAttachments = next
        setValue("attachments", next, { shouldValidate: true, shouldDirty: true })
      }
      reader.readAsDataURL(file)
    })
  }

  const handleRemoveAttachment = (indexToRemove: number) => {
    setErrorFileType(false)
    const next = (attachments || []).filter((_, index) => index !== indexToRemove)
    setValue("attachments", next, { shouldValidate: true, shouldDirty: true })
  }

  useEffect(() => {
    const hasLargeAttachment = (attachments || []).some((file) => file.size > ATTACHMENT_SIZE_LIMIT)
    setErrorLargeAttachment(hasLargeAttachment)
  }, [attachments])

  return (
    <div className="mb-8 flex flex-col">
      <label
        className="py-4"
        htmlFor="form-attachment"
      >
        {m.contact_formAttachment_label({}, { locale: lang })}
      </label>
      <div className="flex flex-col [&_button]:w-[25px] [&_button]:border-0 [&_button]:bg-transparent [&_button]:text-white [&_small]:py-2 [&_small]:text-[11px]">
        <FieldTooltip
          message={errorMessage}
          open={toolTip && errorLargeAttachment}
        >
          <label
            className="mx-auto inline-block w-full cursor-pointer bg-[var(--deep-blue)] p-6 text-[16px] hover:bg-[var(--pink)] [&_i]:px-4 [&_input[type=file]]:hidden [&_p]:flex [&_p]:[overflow-wrap:anywhere] [&_span]:ml-auto"
            htmlFor="form-attachment"
          >
            <input
              type="file"
              id="form-attachment"
              name="attachment"
              onChange={handleAttachmentChange}
              multiple
            />
            <span>
              <i className="fa-solid fa-upload"></i>
              {m.contact_formAttachment_upload({}, { locale: lang })}
            </span>
          </label>
        </FieldTooltip>
        <small>{m.contact_formAttachment_note({}, { locale: lang })}</small>
        {Array.isArray(attachments) && attachments.length > 0 && (
          <div>
            {errorFileType && <p>{m.contact_formAttachment_fileTypeValidation({}, { locale: lang })}</p>}

            {attachments.length > 2 && (
              <p>{m.contact_formAttachment_quantityValidation({}, { locale: lang })}</p>
            )}
            {attachments.map((file, idx) => (
              <div
                key={idx}
                className="flex flex-row [&_p]:w-full [&_p_span]:flex"
                data-id={`contact-attachment-item-${idx}`}
              >
                <p>
                  {file.name}
                  <span>{file.size && formatFileSizeMb(file.size)}</span>
                </p>

                <button
                  data-id={`contact-attachment-remove-${idx}`}
                  type="button"
                  aria-label={`Remove attachment ${file.name}`}
                  onClick={() => handleRemoveAttachment(idx)}
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
              </div>
            ))}

            {errors?.attachments && (
              <p>{(errors.attachments as { message?: string }).message || errorMessage}</p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
